/* eslint-disable react-hooks/exhaustive-deps */
import { Locale } from "@common/constans";
import { TripType } from "@common/models/AvailabilityRequest";
import { IViewport } from "@config/appConfig";
import { Culture, DateTime, ILocale } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import { setToLocaleDawn } from "@utilize/Format";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Icon from "../Icon";
import Modal from "../Modal";
import RangePicker from "../RangePicker";
import { IFormControlProps } from "./Form";

interface IDateRangeDetail {
    name: string;
    label?: string;
    placeholder?: string;
    disable?: boolean;
    icon?: string;
}

export interface IDateRange {
    departure: IDateRangeDetail;
    arrival: IDateRangeDetail;
}

interface IDateRangeInputProps extends IFormControlProps {
    index: number;
    options: IDateRange;
    startRange: number;
    endRange: number;
    maxDateValue?: number;
    minDateValue?: number;
    tripType?: TripType;
    gridCount: number;
    onChange: (name: string, value: any) => void;
    onFlightTypeChange?: (name: string, value: any) => void;
}

const format = (value: number, code: string): string => {
    let dateTime: DateTime = Culture.getDateTimeInstance(code);
    const locale: ILocale = Culture.getLocale(code);
    const dateTimeFormat: string = locale.defaultDateFormat;
    if (!value) { return ""; }
    const timestamp = +value;
    if (!isNaN(timestamp)) {
        dateTime.setTime(timestamp);
    }
    return dateTime.format(dateTimeFormat, false);
}


const DateRangeInput: FC<IDateRangeInputProps> = ({ options, className, name, label, error, required, index, tripType, maxDateValue, minDateValue, disabled, startRange, endRange, gridCount, onChange, onFlightTypeChange }) => {
    const dispatch = useDispatch();
    const rangeRef = useRef<HTMLDivElement>(null);
    const { dir } = Culture.getLocale();
    const IrLocale = Culture.getLocale(Locale.fa);
    const UsLocale = Culture.getLocale(Locale.en);
    const { small, medium } = Config.get<IViewport>("viewport");
    const { localeState, width } = useSelector((state: IAppState) => ({ localeState: state.locale, width: state.windowWidth }));
    const [calenderPosition, setCalenderPosition] = useState<string>("");
    const [isFirst, setIsFirst] = useState<boolean>(true);
    const [showPicker, setShowPicker] = useState<boolean>(false);
    const lastScrollTop = useRef<number>(0);

    useEffect(() => {
        window.addEventListener("mousedown", handleOuterClick);
        window.addEventListener("scroll", scrollWindow);
        return () => {
            window.removeEventListener("mousedown", handleOuterClick);
            window.removeEventListener("scroll", scrollWindow);
        };
    }, []);

    useEffect(() => {
        if (showPicker) {
            let position = "";
            const float = dir === "rtl" ? "left" : "right";
            const dateRangeContainer = document.getElementById("dateRangeInput");
            const dateRangeContainerRect = dateRangeContainer?.getBoundingClientRect();
            if (dateRangeContainerRect) {
                const diff = 600 - dateRangeContainerRect.width;
                if ((float === "left" && dateRangeContainerRect[float] < diff) || (float === "right" && window.innerWidth - dateRangeContainerRect[float] < diff)) {
                    position = float + "-position";
                }
            }
            const dateRangeModal = document.getElementById("dateRangeModal");
            const dateRangeModalRect = dateRangeModal?.getBoundingClientRect();
            if (dateRangeModal && dateRangeModalRect) {
                const end = dateRangeModalRect.top + dateRangeModal?.offsetHeight;
                if (end > window.innerHeight) {
                    setTimeout(() => {
                        window.scrollTo(0, lastScrollTop.current + (end - window.innerHeight) + 10);
                    }, 300);
                }
            }
            setCalenderPosition(position);
        }
    }, [showPicker]);

    const handleOuterClick = (e: any) => {
        if (!rangeRef.current?.contains(e.target)) {
            if (!(e.target.parentElement?.id === "dateRangeModal" || e.target.parentElement?.id === "addReturnDate")) {
                setShowPicker(false);
            }
        }
    }

    const scrollWindow = () => {
        const scroll = window.pageYOffset || window.scrollY;
        lastScrollTop.current = scroll <= 0 ? 0 : scroll; // For Mobile or negative scrolling
    }

    const onDateRangeModeChange = () => {
        dispatch<IAppAction>({ type: AppAction.Locale, payload: { locale: localeState === IrLocale.code ? UsLocale.code : IrLocale.code } });
    }

    const onShowPicker = (e: any) => {
        const name = e.currentTarget.name;
        if (name === "endRange" && startRange)
            setIsFirst(false);
        else if (name === "startRange")
            setIsFirst(true);
        setShowPicker(true);
    }

    const onRangePickerChange = (name: string, value: any) => {
        // dateTime validation, also sets the correct values
        const dateTime = Culture.getDateTimeInstance(localeState);
        const RangeTimestamp = dateTime.validate(value, false, false) ? setToLocaleDawn(dateTime.getTime()) : 0;
        if (name === "startRange")
            onChange(options.departure.name, RangeTimestamp);
        else
            onChange(options.arrival.name, RangeTimestamp);
    }

    const onFirstChange = (isFirst: boolean) => {
        setIsFirst(isFirst);
    }

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const name = e.target.name;
        onRangePickerChange(name, value);
    }

    const onSubmit = () => {
        setShowPicker(false);
        if (tripType !== TripType.MultiCity)
            setTimeout(() => document.getElementById('paxTypeQuantity')?.click(), width <= small ? 500 : 150);
    }

    const sRange = startRange ? format(startRange, localeState) : "";
    const eRange = endRange ? format(endRange, localeState) : "";

    return (
        <div ref={rangeRef} id="dateRangeInput" className={`date-range-input form-group has-icon${className ? ` ${className}` : ""}${error ? " has-error" : ""}`}>
            {label ? <label htmlFor={name}>{label}</label> : null}
            {required ? <Icon name="icon-required" className="filed-required" size="8px" /> : null}
            <div className={`date-range-container ${showPicker ? "focus" : ""}`}>
                <div className="form-container">
                    <Icon name="icon-calendar-outline icon-input" onClick={onShowPicker} />
                    <input
                        className={`${!options.arrival.disable && showPicker && isFirst ? "active" : ""} start-range`}
                        name="startRange"
                        id={`startRange${index}`}
                        value={sRange}
                        placeholder={options.departure.placeholder}
                        disabled={options.departure.disable || disabled}
                        readOnly
                        onChange={onInputChange}
                        onClick={onShowPicker}
                        onFocus={onShowPicker}
                    />
                </div>
                <div className={`date-range-divider${options.arrival.disable ? " disable" : ""}`} />
                <div className={`form-container${options.arrival.disable ? " disable" : ""}`}>
                    <Icon name="icon-calendar-outline icon-input" onClick={onShowPicker} />
                    <input
                        className={`${showPicker && !isFirst ? "active" : ""} end-range`}
                        name="endRange"
                        id={`endRange${index}`}
                        value={eRange}
                        placeholder={options.arrival.placeholder}
                        disabled={options.arrival.disable || disabled}
                        readOnly
                        onChange={onInputChange}
                        onClick={onShowPicker}
                    />
                </div>
            </div>
            <p className="form-error">{error || ""}</p>
            <Modal
                id="dateRangeModal"
                className={calenderPosition}
                show={showPicker}
                name={width <= small ? "slide-up" : "fade"}
                mode={width <= medium ? "full" : "inline"}
                onClose={() => setShowPicker(false)}
            >
                <RangePicker
                    gridCount={gridCount}
                    startRange={sRange}
                    endRange={eRange}
                    maxDateValue={maxDateValue}
                    minDateValue={minDateValue}
                    isFirst={isFirst}
                    singleSelect={options.arrival.disable || false}
                    options={options}
                    onChange={onRangePickerChange}
                    onFirstChange={onFirstChange}
                    onSubmit={onSubmit}
                    onAbort={() => setShowPicker(false)}
                    onDateRangeModeChange={onDateRangeModeChange}
                    onFlightTypeChange={onFlightTypeChange}
                />
            </Modal>
        </div>
    );
}

export default DateRangeInput;
